<template>
    <Head title="Maintenance Settings" />

    <div class="max-w-7xl mx-auto space-y-6 py-6 px-4 sm:px-6 lg:px-8">
      <!-- Page Header -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div>
            <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">Maintenance Settings</h1>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-400">
              Configure maintenance services, categories, SLA policies, and assets
            </p>
          </div>
          <InfoButton title="Maintenance Settings Hub" variant="default">
            <p>
              <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
              The Maintenance Settings hub provides access to all configuration options for your maintenance
              management system. This page serves as a central navigation point to set up service catalogs,
              asset hierarchies, SLA policies, notification rules, and preventive maintenance plans. Proper
              configuration of these settings ensures efficient work order management, automated scheduling,
              and clear accountability across your maintenance operations.
            </p>
            <p>
              <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
              Click any card to access that settings area:<br>
              • <strong>Service Categories:</strong> Create hierarchical groupings for maintenance work types
                (e.g., Electrical, Plumbing, HVAC). Used for organizing services and reporting<br>
              • <strong>Maintenance Services:</strong> Define specific maintenance tasks that can be assigned
                to work orders (e.g., "Replace HVAC Filter," "Inspect Fire System"). Each service can have
                default time estimates and categories<br>
              • <strong>Maintenance SLA Policies:</strong> Set response and resolution time targets based on
                work order priority and type. SLA breaches trigger alerts and reports<br>
              • <strong>Notification & Routing Policies:</strong> Configure who receives email notifications
                and routing when work orders are created, updated, or completed based on type, category, and priority<br>
              • <strong>Assets:</strong> Manage equipment and machinery requiring maintenance. Create asset
                records, assign categories, track locations, and configure PM schedules<br>
              • <strong>PM Schedule Settings (PM Plans):</strong> Create reusable preventive maintenance plan
                templates with task checklists and frequencies. Link plans to assets to automatically generate
                scheduled work orders
            </p>
            <p>
              <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
              • <strong>Start with categories</strong> - Set up Service Categories and Asset Categories first
                before adding services and assets<br>
              • <strong>SLA policies drive priorities</strong> - Configure SLAs to ensure critical work gets
                immediate attention<br>
              • <strong>Notification policies prevent gaps</strong> - Ensure the right people are notified
                for different work types<br>
              • <strong>PM Plans save time</strong> - Create reusable templates for common maintenance tasks
                like monthly inspections or annual certifications<br>
              • <strong>Test with sample data</strong> - Create a few test assets and services to validate your
                setup before rolling out system-wide<br>
              • <strong>Regular review</strong> - Revisit these settings quarterly to optimize based on actual
                usage patterns
            </p>
          </InfoButton>
        </div>
      </div>

      <!-- Settings Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <!-- Service Categories Card -->
        <Link :href="route('settings.maintenance.service-categories.index')">
          <Card class="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <div class="p-6">
              <div class="flex items-center space-x-4 mb-4">
                <div class="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                  <svg class="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">Service Categories</h3>
                </div>
                <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                Organize maintenance work into categories like electrical, plumbing, HVAC, and more
              </p>
            </div>
          </Card>
        </Link>

        <!-- Maintenance Services Card -->
        <Link :href="route('settings.maintenance.services.index')">
          <Card class="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <div class="p-6">
              <div class="flex items-center space-x-4 mb-4">
                <div class="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                  <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">Maintenance Services</h3>
                </div>
                <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                Define specific maintenance services that can be assigned to work orders
              </p>
            </div>
          </Card>
        </Link>

        <!-- Maintenance SLA Policies Card -->
        <Link :href="route('settings.maintenance.sla-policies.index')">
          <Card class="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <div class="p-6">
              <div class="flex items-center space-x-4 mb-4">
                <div class="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                  <svg class="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">Maintenance SLA Policies</h3>
                </div>
                <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                Set response and resolution time targets for work orders based on priority and type
              </p>
            </div>
          </Card>
        </Link>

        <!-- Work Order Notification Policies Card -->
        <Link :href="route('settings.maintenance.notification-policies.index')">
          <Card class="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <div class="p-6">
              <div class="flex items-center space-x-4 mb-4">
                <div class="w-12 h-12 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center flex-shrink-0">
                  <svg class="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">Notification & Routing Policies</h3>
                </div>
                <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                Define who gets notified and routed when work orders are created based on type, category, and priority
              </p>
            </div>
          </Card>
        </Link>

        <!-- Assets Card -->
        <Link v-if="subscriptionFeatures.asset_module" :href="route('settings.maintenance.assets.index')">
          <Card class="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <div class="p-6">
              <div class="flex items-center space-x-4 mb-4">
                <div class="w-12 h-12 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0">
                  <svg class="w-6 h-6 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">Assets</h3>
                </div>
                <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                Manage equipment and assets that require maintenance tracking and work orders
              </p>
            </div>
          </Card>
        </Link>

        <!-- PM Schedule Settings Card -->
        <Link v-if="subscriptionFeatures.asset_module" :href="route('settings.maintenance.pm-plans.index')">
          <Card class="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <div class="p-6">
              <div class="flex items-center space-x-4 mb-4">
                <div class="w-12 h-12 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                  <svg class="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">PM Schedule Settings</h3>
                </div>
                <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                Create and manage preventive maintenance plan templates with tasks and schedules
              </p>
            </div>
          </Card>
        </Link>
      </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { Head, Link, usePage } from '@inertiajs/vue3';
import Card from '@/Components/Common/Card.vue';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

// Access subscription features from Inertia shared props
const page = usePage();
const subscriptionFeatures = computed(() => page.props.subscriptionFeatures || { asset_module: false, equipment_module: false });
</script>
